import type { SuKien } from './types.ts';

/**
 * Câu hiển thị của một sự kiện — port `cau-hien-thi.ts` của native.
 *
 * Danh sách nhật ký **không bao giờ hiện mã thô**: mỗi dòng đổi `hanh_dong` thành một câu. Khác native ở chỗ câu nằm ở
 * hai catalog dịch (`nktvDanhMuc.<mã>`, xem `danh-muc.ts` vì sao không đọc thẳng `v1.json`), nên tệp này nhận MẪU CÂU
 * của ngôn ngữ đang xem chứ không tự tra mã. Hook `useCauSuKien` (`../hooks/useCauSuKien.ts`) nối hai đầu.
 *
 * Cú pháp placeholder giữ nguyên của danh mục — MỘT cặp ngoặc `{doi_tuong}`, `{thay_doi.vai_tro.truoc}` — nên i18next
 * (chỉ nội suy `{{x}}`) trả mẫu nguyên văn và việc thế chỗ chờ làm ở đây, đúng luật của gói danh mục native.
 */

const DANG_CHO = /\{([a-z0-9_.]+)\}/g;

/** Hình dạng tối thiểu để dựng câu — mọi `SuKien` đều khớp. */
export type NguonCau = Pick<SuKien, 'doi_tuong' | 'thay_doi' | 'du_lieu_them'> &
  Partial<Pick<SuKien, 'nguoi_thuc_hien' | 'ket_qua' | 'nguon'>>;

/** Ba chữ chèn vào chỗ chờ, đã dịch theo ngôn ngữ đang xem (`nktv.cau.trong`, `nktv.giaTri.co`, `nktv.giaTri.khong`). */
export interface ChuChen {
  /** Chỗ thiếu dữ liệu — native điền `(trống)`, không ném lỗi. */
  trong: string;
  co: string;
  khong: string;
}

/** Mọi đường dẫn placeholder của một mẫu câu, theo thứ tự xuất hiện. */
export function choChoCua(mau: string): string[] {
  return [...mau.matchAll(DANG_CHO)].map((k) => k[1] as string);
}

function traDuongDan(suKien: NguonCau, duong: string): unknown {
  // `doi_tuong` đứng một mình là lối viết tắt được phép cho `ten_hien_thi`.
  if (duong === 'doi_tuong') {
    const doiTuong = suKien.doi_tuong;
    if (!doiTuong) return null;
    return doiTuong.ten_hien_thi || doiTuong.id;
  }
  let nut: unknown = suKien;
  for (const khuc of duong.split('.')) {
    if (nut === null || typeof nut !== 'object') return null;
    nut = (nut as Record<string, unknown>)[khuc];
  }
  return nut;
}

/**
 * Thế chỗ chờ của `mau` bằng dữ liệu của sự kiện. Chỗ thiếu điền `chu.trong`, boolean thành `chu.co`/`chu.khong` —
 * đúng luật `cauCho` của gói danh mục native.
 */
export function theCau(mau: string, suKien: NguonCau, chu: ChuChen): string {
  return mau.replace(DANG_CHO, (_khop, duong: string) => {
    const giaTri = traDuongDan(suKien, duong);
    if (giaTri === null || giaTri === undefined || giaTri === '') return chu.trong;
    if (giaTri === true) return chu.co;
    if (giaTri === false) return chu.khong;
    if (typeof giaTri === 'object') return JSON.stringify(giaTri);
    return String(giaTri);
  });
}

/**
 * Nhãn ngắn từ mẫu câu, cho ô chọn "Thao tác": bỏ hết placeholder và dấu câu rơi lại. Không dùng `theCau(mau, {})` vì
 * nó cho ra "Đổi quyền của (trống): (trống) → (trống)".
 *
 * Trả `null` khi mẫu bỏ hết placeholder thì không còn chữ nào — nơi gọi rơi về mã.
 */
export function nhanTuMau(mau: string): string | null {
  const nhan = mau
    .split(' · ')
    // Vế mở đầu bằng một chỗ chờ ("{tong_token} token") bỏ chỗ chờ đi chỉ còn lại đơn vị trơ trọi.
    .filter((ve, i) => i === 0 || !ve.trimStart().startsWith('{'))
    .join(' · ')
    .replace(DANG_CHO, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[\s·:,\-–—]*→[\s·:,\-–—]*$/, '')
    .replace(/[\s·:,]+$/, '')
    .trim();
  return nhan === '' ? null : nhan;
}

/**
 * Tách câu quanh tên đối tượng để nơi gọi bọc đúng phần ấy thành nút lọc ("bấm vào tên đối tượng là lọc ngay").
 * `null` khi tên không nằm trong câu — nơi gọi hiện câu trần.
 */
export function tachTheoDoiTuong(
  cau: string,
  ten: string | null | undefined,
): { truoc: string; giua: string; sau: string } | null {
  if (!ten) return null;
  const viTri = cau.indexOf(ten);
  if (viTri < 0) return null;
  return {
    truoc: cau.slice(0, viTri),
    giua: ten,
    sau: cau.slice(viTri + ten.length),
  };
}

/** Mã của sự kiện gọi mô hình. */
export const MA_LLM_GOI = 'llm.goi';

/**
 * Token Fox của một lượt gọi mô hình mà API không trả token thật: tenant chỉ được thấy token Fox (ADR-0123 mục 5), nên
 * câu của danh mục (đọc `tong_token`) không dùng được. `null` khi không phải ca ấy.
 */
export function llmTokenFox(suKien: Pick<SuKien, 'hanh_dong' | 'du_lieu_them'>): number | null {
  if (suKien.hanh_dong !== MA_LLM_GOI) return null;
  const them = suKien.du_lieu_them ?? {};
  if (typeof them['tong_token'] === 'number') return null;
  const vao = them['fox_dau_vao'];
  const ra = them['fox_dau_ra'];
  return typeof vao === 'number' && typeof ra === 'number' ? vao + ra : null;
}

/**
 * Lượt gọi mô hình mà nhà cung cấp KHÔNG báo số token: câu của danh mục sẽ ra "… · (trống) token" — đọc như lỗi hiển
 * thị trong khi đó là một sự thật có tên. Trả tên tính năng (chuỗi rỗng khi không rõ) để nơi gọi dựng câu riêng
 * (`nktv.cau.llmChuaDoToken`); `null` khi không phải ca ấy.
 */
export function llmChuaDoToken(suKien: Pick<SuKien, 'hanh_dong' | 'du_lieu_them'>): string | null {
  if (suKien.hanh_dong !== MA_LLM_GOI) return null;
  const them = suKien.du_lieu_them ?? {};
  if (typeof them['tong_token'] === 'number' || llmTokenFox(suKien) !== null) return null;
  const tinhNang = them['tinh_nang'];
  return typeof tinhNang === 'string' ? tinhNang : '';
}
